import React, { useState } from 'react';
import { ArrowRight, Database, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/GlassCard';
import { FeatureModal } from '../components/FeatureModal';
interface HomePageProps {
  onNavigate: (section: string) => void;
  isDark: boolean;
}
const featureContent = {
  unified: {
    description: 'Akios brings together ERP and CRM into one seamless platform, eliminating data silos and reducing the complexity of managing multiple systems. Everything your business needs, accessible from a single dashboard.',
    features: ['Single source of truth for all business data - no more reconciling between systems', 'Unified customer view combining sales, support, and financial history', 'Integrated inventory and order management with real-time synchronization', 'Centralized reporting across all departments and functions', 'One login, one interface, one platform for your entire team'],
    benefits: ['Reduce software costs by 40-60%', 'Eliminate duplicate data entry', 'Faster decision-making with complete data', 'Improved team collaboration', 'Simplified IT management', 'Better data accuracy and consistency']
  },
  realtime: {
    description: 'Make informed decisions with live data that updates instantly across your entire organization. Our real-time analytics engine processes millions of data points to give you actionable insights exactly when you need them.',
    features: ['Live dashboards that update automatically as data changes', 'Real-time inventory tracking across all warehouses and locations', 'Instant sales performance metrics and pipeline visibility', 'Automated alerts for critical business events and thresholds', 'Custom KPI tracking with visual indicators and trend analysis', 'Mobile access to real-time data from anywhere'],
    benefits: ['Respond to market changes instantly', 'Prevent stockouts and overstock situations', 'Identify sales opportunities faster', 'Reduce response time to customer issues', 'Optimize resource allocation in real-time', 'Make data-driven decisions on the fly']
  },
  security: {
    description: 'Your business data is protected by military-grade security protocols. We implement multiple layers of protection, from encryption to access controls, ensuring your sensitive information stays safe and compliant.',
    features: ['AES-256 encryption for data at rest and in transit', 'Multi-factor authentication (MFA) and single sign-on (SSO)', 'Role-based access control with granular permissions', 'Automated security audits and compliance monitoring', 'SOC 2 Type II, ISO 27001, and GDPR compliant', 'Regular penetration testing and security updates', 'Automated backups with point-in-time recovery'],
    benefits: ['Protect sensitive customer and financial data', 'Meet industry compliance requirements', 'Prevent unauthorized access', 'Reduce risk of data breaches', 'Maintain business continuity', 'Build customer trust and confidence']
  }
};
export function HomePage({
  onNavigate,
  isDark
}: HomePageProps) {
  const [activeModal, setActiveModal] = useState<'unified' | 'realtime' | 'security' | null>(null);
  return <div className="page-transition pt-24 pb-16 px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-271">
      {/* Hero Section */}
      <div className="relative min-h-[80vh] flex flex-col justify-center items-center text-center mb-24" data-id="element-272">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-500/10 rounded-full blur-[100px] -z-10" data-id="element-273" />

        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel shadow-sm mb-8 animate-[fadeInUp_0.6s_ease-out]" data-id="element-274">
          <span className="w-2 h-2 rounded-full bg-brand-500 animate-pulse" data-id="element-275" />
          <span className="text-sm font-medium text-gray-600 dark:text-gray-300" data-id="element-276">
            Think bold. Build brilliant.
          </span>
        </div>

        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8 leading-[1.1] animate-[fadeInUp_0.6s_ease-out_0.1s_forwards] opacity-0 translate-y-4" data-id="element-277">
          All-in-One Business <br data-id="element-278" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-500 to-blue-600" data-id="element-279">
            Operating System
          </span>
        </h1>

        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mb-10 leading-relaxed animate-[fadeInUp_0.6s_ease-out_0.2s_forwards] opacity-0 translate-y-4" data-id="element-280">
          AKIOS is a modular Business Operating System designed to help small
          and mid‑sized businesses run their entire operations through one
          unified platform. It combines the most critical business functions
          like Sales, CRM, Inventory, Billing, Service/Operations, Staff,
          Compliance, and Analytics into one connected workflow.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 animate-[fadeInUp_0.6s_ease-out_0.3s_forwards] opacity-0 translate-y-4" data-id="element-281">
          <Button size="lg" onClick={() => onNavigate('contact')} data-id="element-282">
            Request Demo
            <ArrowRight className="ml-2 w-4 h-4" data-id="element-283" />
          </Button>
          <Button variant="outline" size="lg" onClick={() => onNavigate('services')} data-id="element-284">
            Explore Features
          </Button>
        </div>
      </div>

      {/* Quick Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" data-id="element-285">
        <GlassCard hoverEffect className="animate-[fadeInUp_0.6s_ease-out_0.4s_forwards] opacity-0 translate-y-4 group" onClick={() => setActiveModal('unified')} data-id="element-286">
          <div className="w-12 h-12 bg-brand-50 dark:bg-brand-900/30 rounded-xl flex items-center justify-center mb-6 transition-colors group-hover:bg-brand-100 dark:group-hover:bg-brand-800/50" data-id="element-287">
            <Database className="w-6 h-6 text-brand-600 dark:text-brand-400" data-id="element-288" />
          </div>
          <h3 className="text-xl font-bold mb-3 transition-colors group-hover:text-brand-600 dark:group-hover:text-brand-400" data-id="element-289">
            Unified Platform
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-290">
            One system for ERP and CRM. No more juggling between multiple tools
            and disconnected data.
          </p>
          <div className="text-brand-600 dark:text-brand-400 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-291">
            Learn more <ArrowRight className="w-4 h-4" data-id="element-292" />
          </div>
        </GlassCard>

        <GlassCard hoverEffect className="animate-[fadeInUp_0.6s_ease-out_0.5s_forwards] opacity-0 translate-y-4 group" onClick={() => setActiveModal('realtime')} data-id="element-293">
          <div className="w-12 h-12 bg-brand-50 dark:bg-brand-900/30 rounded-xl flex items-center justify-center mb-6 transition-colors group-hover:bg-brand-100 dark:group-hover:bg-brand-800/50" data-id="element-294">
            <Zap className="w-6 h-6 text-brand-600 dark:text-brand-400" data-id="element-295" />
          </div>
          <h3 className="text-xl font-bold mb-3 transition-colors group-hover:text-brand-600 dark:group-hover:text-brand-400" data-id="element-296">
            Real-Time Insights
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-297">
            Live dashboards and analytics that give you instant visibility into
            operations and customer data.
          </p>
          <div className="text-brand-600 dark:text-brand-400 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-298">
            Learn more <ArrowRight className="w-4 h-4" data-id="element-299" />
          </div>
        </GlassCard>

        <GlassCard hoverEffect className="animate-[fadeInUp_0.6s_ease-out_0.6s_forwards] opacity-0 translate-y-4 group" onClick={() => setActiveModal('security')} data-id="element-300">
          <div className="w-12 h-12 bg-brand-50 dark:bg-brand-900/30 rounded-xl flex items-center justify-center mb-6 transition-colors group-hover:bg-brand-100 dark:group-hover:bg-brand-800/50" data-id="element-301">
            <Shield className="w-6 h-6 text-brand-600 dark:text-brand-400" data-id="element-302" />
          </div>
          <h3 className="text-xl font-bold mb-3 transition-colors group-hover:text-brand-600 dark:group-hover:text-brand-400" data-id="element-303">
            Enterprise Security
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-304">
            Bank-grade encryption and compliance standards built into every
            layer of the platform.
          </p>
          <div className="text-brand-600 dark:text-brand-400 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-305">
            Learn more <ArrowRight className="w-4 h-4" data-id="element-306" />
          </div>
        </GlassCard>
      </div>

      {/* Feature Modals */}
      <FeatureModal isOpen={activeModal === 'unified'} onClose={() => setActiveModal(null)} title="Unified Platform" content={featureContent.unified} isDark={isDark} data-id="element-307" />
      <FeatureModal isOpen={activeModal === 'realtime'} onClose={() => setActiveModal(null)} title="Real-Time Insights" content={featureContent.realtime} isDark={isDark} data-id="element-308" />
      <FeatureModal isOpen={activeModal === 'security'} onClose={() => setActiveModal(null)} title="Enterprise Security" content={featureContent.security} isDark={isDark} data-id="element-309" />
    </div>;
}